import { ChevronRight } from 'lucide-react';
import { useState, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { EGSwitchOrgModal } from './eg-switch-org-modal';
import { EGUserDisplay } from './eg-user-display';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import useAuth from '@/hooks/useAuth';
import { stringSortCompare } from '@/hooks/useSort';
import { cn } from '@/lib/utils';
import { useOrgsStore } from '@/stores/orgs';
import { useUserStore } from '@/stores/user';

interface EGHeaderProps {
  isAuthed?: boolean;
}

export function EGHeader({ isAuthed = false }: EGHeaderProps) {
  const { pathname } = useLocation();
  const userStore = useUserStore();
  const orgs = useOrgsStore((s) => s.orgs);
  const navigate = useNavigate();
  const { signOutAndRedirect } = useAuth();

  const [acctDropdownIsOpen, setAcctDropdownIsOpen] = useState(false);
  const [switchToOrgId, setSwitchToOrgId] = useState<string | null>(null);
  const [switchOrgDialogOpen, setSwitchOrgDialogOpen] = useState(false);

  const labsPath = '/labs';
  const orgsPath = '/orgs';
  const helpPath = '/help';
  const usageDashboardPath = userStore.isSuperuser() ? '/admin/usage-dashboard' : '/usage-dashboard';

  function isSubpath(url: string) {
    return pathname.includes(url);
  }

  function selectSwitchToOrg(orgId: string) {
    setSwitchToOrgId(orgId);
    setSwitchOrgDialogOpen(true);
    setAcctDropdownIsOpen(false);
  }

  function handleRunPipelineShortcut() {
    const labId = userStore.mostRecentLabId();
    if (labId) {
      void navigate(
        `/labs/${labId}/run-pipeline/${import.meta.env.VITE_PIPELINE_SHORTCUT_PIPELINE_ID}?seqeraRunTempId=${uuidv4()}`,
      );
    } else {
      void navigate('/labs');
    }
  }

  const otherOrgs = useMemo(
    () =>
      Object.values(orgs)
        .filter((org) => org.OrganizationId !== userStore.currentOrgId())
        .sort((a, b) => stringSortCompare(a.Name, b.Name)),
    [orgs, userStore],
  );

  const currentOrgName = orgs[userStore.currentOrgId() ?? '']?.Name;

  return (
    <>
      <header className="flex h-[78px] w-full flex-row items-center justify-center bg-white">
        <div
          className={cn(
            'header-container max-w-[var(--max-page-container-width-px)]',
            isAuthed && 'flex w-full flex-row items-center justify-between overflow-x-clip pl-4',
          )}
        >
          {isAuthed ? (
            <>
              <img className="mr-2 w-[105px]" src="/assets/images/helix-star-logo.svg" alt="Helix Star logo" />

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-6">
                  {!userStore.isSuperuser() && import.meta.env.VITE_PIPELINE_SHORTCUT_LABEL && (
                    <button
                      onClick={handleRunPipelineShortcut}
                      className={cn(
                        "nav-link rounded-3xl px-[11px] py-2 font-['PlusJakartaSans',serif] text-[15px] leading-[26px] font-medium tracking-[0.46px] whitespace-nowrap capitalize",
                        isSubpath('/run-pipeline/') ? 'text-primary' : 'text-gray-900',
                      )}
                    >
                      {import.meta.env.VITE_PIPELINE_SHORTCUT_LABEL}
                    </button>
                  )}
                  {!userStore.isSuperuser() && (
                    <Link
                      to={labsPath}
                      className={cn(
                        "nav-link rounded-3xl px-[11px] py-2 font-['PlusJakartaSans',serif] text-[15px] leading-[26px] font-medium tracking-[0.46px] whitespace-nowrap capitalize",
                        isSubpath(labsPath) ? 'text-primary' : 'text-gray-900',
                      )}
                    >
                      Labs
                    </Link>
                  )}
                  {userStore.canManageAnyOrgs() && (
                    <Link
                      to={orgsPath}
                      className={cn(
                        "nav-link rounded-3xl px-[11px] py-2 font-['PlusJakartaSans',serif] text-[15px] leading-[26px] font-medium tracking-[0.46px] whitespace-nowrap capitalize",
                        isSubpath(orgsPath) ? 'text-primary' : 'text-gray-900',
                      )}
                    >
                      Organizations
                    </Link>
                  )}
                  <Link
                    to={helpPath}
                    className={cn(
                      "nav-link rounded-3xl px-[11px] py-2 font-['PlusJakartaSans',serif] text-[15px] leading-[26px] font-medium tracking-[0.46px] whitespace-nowrap capitalize",
                      isSubpath(helpPath) ? 'text-primary' : 'text-gray-900',
                    )}
                  >
                    Help & Instruction
                  </Link>
                  <Link
                    to={usageDashboardPath}
                    className={cn(
                      "nav-link rounded-3xl px-[11px] py-2 font-['PlusJakartaSans',serif] text-[15px] leading-[26px] font-medium tracking-[0.46px] whitespace-nowrap capitalize",
                      isSubpath('usage-dashboard') ? 'text-primary' : 'text-gray-900',
                    )}
                  >
                    Usage Dashboard
                  </Link>
                </div>

                {!userStore.isSuperuser() && currentOrgName && (
                  <span className="border-primary text-primary inline-flex items-center rounded-full border px-2.5 py-0.5 font-serif text-[13px] leading-[18px] tracking-[0.16px]">
                    {currentOrgName}
                  </span>
                )}

                <DropdownMenu open={acctDropdownIsOpen} onOpenChange={setAcctDropdownIsOpen}>
                  <DropdownMenuTrigger className="outline-none">
                    <EGUserDisplay initials={userStore.currentUserInitials()} />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 rounded-lg bg-white p-0 shadow-lg ring-0">
                    <div
                      className={cn(
                        'bg-background-light-grey flex w-full cursor-pointer flex-row items-center gap-3 p-4',
                        userStore.isSuperuser() && 'cursor-default',
                      )}
                      onClick={() => {
                        if (!userStore.isSuperuser()) {
                          setAcctDropdownIsOpen(false);
                          void navigate('/profile');
                        }
                      }}
                    >
                      <EGUserDisplay
                        className="grow"
                        initials={userStore.currentUserInitials()}
                        name={userStore.currentUserDisplayName()}
                        organization={currentOrgName ?? null}
                      />
                      {!userStore.isSuperuser() && <ChevronRight className="size-6" />}
                    </div>

                    {!userStore.isSuperuser() && otherOrgs.length > 0 && (
                      <div className="bg-background-light-grey px-4">
                        <div className="text-muted pt-2 text-sm">Other Organizations</div>
                        {otherOrgs.map((org, i) => (
                          <div key={org.OrganizationId}>
                            {i > 0 && <div className="w-full border-t" />}
                            <div
                              className="flex w-full cursor-pointer items-center justify-between py-3 text-left"
                              onClick={() => selectSwitchToOrg(org.OrganizationId)}
                            >
                              <div className="truncate font-medium">{org.Name}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <DropdownMenuSeparator className="m-0" />
                    <DropdownMenuItem
                      onClick={signOutAndRedirect}
                      className="text-primary cursor-pointer p-4 underline"
                    >
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </>
          ) : (
            <div className="center flex flex-col justify-center text-center">
              <img className="mr-2 w-[140px]" src="/assets/images/helix-star-logo.svg" alt="Helix Star logo" />
            </div>
          )}
        </div>
      </header>

      <EGSwitchOrgModal
        open={switchOrgDialogOpen}
        onOpenChange={setSwitchOrgDialogOpen}
        switchToOrgId={switchToOrgId}
      />
    </>
  );
}

export default EGHeader;
